import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDebounce } from 'react-use';
import type { ValueOf } from 'type-fest';

import {
  Filter,
  SearchParams,
  useSearchOrderItems,
  useSearchOrders,
  useSearchParts,
  useSearchQuoteItems,
  useSearchQuotes,
  useSearchShares,
} from '@frontend/api';
import type {
  UseSearchPartsParams,
  UseSearchSharesParams,
  UseSearchQuoteItemsParams,
  UseSearchOrdersParams,
  UseSearchQuotesParams,
  UseSearchOrderItemsParams,
} from '@frontend/api';

import { PERMISSIONS, SUPPLIER_ROLES } from 'constants/all';

import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';

import { useFeatureFlagValue } from 'store/session/hooks';
import { getGroupMemberSupplierRoles } from 'store/session/selectors';

import {
  TABS,
  DEFAULT_FILTER_SIZE,
  DEFAULT_PAGINATION_PARAMS,
  PAGINATION_SIZES,
} from './constants';

export type ParamsState<T extends SearchParams = SearchParams> = Omit<
  T,
  'query'
>;

export type SearchDataContextType =
  | {
      userHasTyped: boolean;

      activeTab: string;
      setActiveTab: React.Dispatch<React.SetStateAction<ValueOf<typeof TABS>>>;
      searchTerm: string;
      setSearchTerm: React.Dispatch<React.SetStateAction<string>>;

      data: {
        parts: ReturnType<typeof useSearchParts>['data'];
        shares: ReturnType<typeof useSearchShares>['data'];
        quotes: ReturnType<typeof useSearchQuotes>['data'];
        quoteItems: ReturnType<typeof useSearchQuoteItems>['data'];
        orders: ReturnType<typeof useSearchOrders>['data'];
        orderItems: ReturnType<typeof useSearchOrderItems>['data'];
      };

      query: {
        parts: UseSearchPartsParams;
        shares: UseSearchSharesParams;
        quotes: UseSearchQuotesParams;
        quoteItems: UseSearchQuoteItemsParams;
        orders: UseSearchOrdersParams;
        orderItems: UseSearchOrderItemsParams;
      };
    }
  | undefined;

export const buildGenericDefaultFilter = (): Filter => ({
  values: [],
  size: DEFAULT_FILTER_SIZE,
});

export const buildAccountNamesDefaultFilter = (): Filter<'accountNames'> => ({
  accountNames: [],
  size: DEFAULT_FILTER_SIZE,
});

export const buildProcessMaterialFinishDefaultFilter = (): {
  process: Filter;
  materials: Filter;
  finishes: Filter;
} => ({
  process: buildGenericDefaultFilter(),
  materials: buildGenericDefaultFilter(),
  finishes: buildGenericDefaultFilter(),
});

const SearchDataContext = createContext<SearchDataContextType>(undefined);

const initialParams = {
  parts: {
    ...DEFAULT_PAGINATION_PARAMS,
    filters: buildProcessMaterialFinishDefaultFilter(),
  },
  shares: {
    ...DEFAULT_PAGINATION_PARAMS,
    filters: {
      businessNames: buildGenericDefaultFilter(),
    },
  },
  quotes: {
    ...DEFAULT_PAGINATION_PARAMS,
    filters: {
      status: [],
      accountNames: buildAccountNamesDefaultFilter(),
    },
  },
  quoteItems: {
    ...DEFAULT_PAGINATION_PARAMS,
    filters: buildProcessMaterialFinishDefaultFilter(),
  },
  orders: {
    ...DEFAULT_PAGINATION_PARAMS,
    filters: {
      status: [],
      accountNames: buildAccountNamesDefaultFilter(),
    },
  },
  orderItems: {
    ...DEFAULT_PAGINATION_PARAMS,
    filters: buildProcessMaterialFinishDefaultFilter(),
  },
} as const;

export const SearchDataContextProvider = ({
  children,
}: React.PropsWithChildren) => {
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState(debouncedSearchTerm);
  const [userHasTyped, setUserHasTyped] = useState(false);
  useDebounce(() => setDebouncedSearchTerm(searchTerm), 800, [searchTerm]);
  const [activeTab, setActiveTab] = useState<ValueOf<typeof TABS>>(TABS.ALL);

  const searchTermIsNotEmpty = debouncedSearchTerm.trim() !== '';

  const permissions: Record<string, string> = useSelector(
    getGroupMemberSupplierRoles,
  );
  const isQuotingEnabled: boolean = useFeatureFlagValue(
    featureFlagKeys.isQuotingToolAvailable,
  );

  const canSeeQuotes =
    isQuotingEnabled &&
    permissions[SUPPLIER_ROLES.QUOTES] !== PERMISSIONS.NO_ACCESS;
  const canSeeOrders =
    canSeeQuotes &&
    permissions[SUPPLIER_ROLES.ORDERS] !== PERMISSIONS.NO_ACCESS;

  const baseQueryOptions = useMemo(
    () => ({
      enabled: searchTermIsNotEmpty,
      keepPreviousData: true,
    }),
    [searchTermIsNotEmpty],
  );

  const partsQuery = useMemo(
    () => ({
      query: debouncedSearchTerm,
      ...initialParams.parts,
      paginateSize:
        activeTab === TABS.PARTS
          ? PAGINATION_SIZES.FULL
          : PAGINATION_SIZES.SHORT,
    }),
    [debouncedSearchTerm, activeTab],
  );
  const parts = useSearchParts(partsQuery, {
    queryOptions: {
      ...baseQueryOptions,
      enabled:
        baseQueryOptions.enabled &&
        (activeTab === TABS.ALL || activeTab !== TABS.PARTS),
    },
  });
  const sharesQuery = useMemo(
    () => ({
      query: debouncedSearchTerm,
      ...initialParams.shares,
      paginateSize:
        activeTab === TABS.SHARES
          ? PAGINATION_SIZES.FULL
          : PAGINATION_SIZES.SHORT,
    }),
    [debouncedSearchTerm, activeTab],
  );
  const shares = useSearchShares(sharesQuery, {
    queryOptions: {
      ...baseQueryOptions,
      enabled:
        baseQueryOptions.enabled &&
        (activeTab === TABS.ALL || activeTab !== TABS.SHARES),
    },
  });
  const quotesQuery = useMemo(
    () => ({
      query: debouncedSearchTerm,
      ...initialParams.quotes,
      paginateSize:
        activeTab === TABS.QUOTES
          ? PAGINATION_SIZES.FULL
          : PAGINATION_SIZES.SHORT,
    }),
    [debouncedSearchTerm, activeTab],
  );
  const quotes = useSearchQuotes(quotesQuery, {
    queryOptions: {
      ...baseQueryOptions,
      enabled:
        baseQueryOptions.enabled &&
        canSeeQuotes &&
        (activeTab === TABS.ALL || activeTab !== TABS.QUOTES),
    },
  });
  const quoteItemsQuery = useMemo(
    () => ({
      query: debouncedSearchTerm,
      ...initialParams.quoteItems,
      paginateSize:
        activeTab === TABS.QUOTE_ITEMS
          ? PAGINATION_SIZES.FULL
          : PAGINATION_SIZES.SHORT,
    }),
    [debouncedSearchTerm, activeTab],
  );
  const quoteItems = useSearchQuoteItems(quoteItemsQuery, {
    queryOptions: {
      ...baseQueryOptions,
      enabled:
        baseQueryOptions.enabled &&
        canSeeQuotes &&
        (activeTab === TABS.ALL || activeTab !== TABS.QUOTE_ITEMS),
    },
  });
  const ordersQuery = useMemo(
    () => ({
      query: debouncedSearchTerm,
      ...initialParams.orders,
      paginateSize:
        activeTab === TABS.ORDERS
          ? PAGINATION_SIZES.FULL
          : PAGINATION_SIZES.SHORT,
    }),
    [debouncedSearchTerm, activeTab],
  );
  const orders = useSearchOrders(ordersQuery, {
    queryOptions: {
      ...baseQueryOptions,
      enabled:
        baseQueryOptions.enabled &&
        canSeeOrders &&
        (activeTab === TABS.ALL || activeTab !== TABS.ORDERS),
    },
  });
  const orderItemsQuery = useMemo(
    () => ({
      query: debouncedSearchTerm,
      ...initialParams.orderItems,
      paginateSize:
        activeTab === TABS.ORDER_ITEMS
          ? PAGINATION_SIZES.FULL
          : PAGINATION_SIZES.SHORT,
    }),
    [debouncedSearchTerm, activeTab],
  );
  const orderItems = useSearchOrderItems(orderItemsQuery, {
    queryOptions: {
      ...baseQueryOptions,
      enabled:
        baseQueryOptions.enabled &&
        canSeeOrders &&
        (activeTab === TABS.ALL || activeTab !== TABS.ORDER_ITEMS),
    },
  });

  useEffect(() => {
    if (
      searchTerm === debouncedSearchTerm &&
      !parts.isFetching &&
      !shares.isFetching &&
      !quotes.isFetching &&
      !quoteItems.isFetching &&
      !orders.isFetching &&
      !orderItems.isFetching
    ) {
      setUserHasTyped(false);
    }
  }, [
    searchTerm,
    debouncedSearchTerm,
    parts.isFetching,
    shares.isFetching,
    quotes.isFetching,
    quoteItems.isFetching,
    orders.isFetching,
    orderItems.isFetching,
  ]);

  return (
    <SearchDataContext.Provider
      value={{
        query: {
          parts: partsQuery,
          shares: sharesQuery,
          quotes: quotesQuery,
          quoteItems: quoteItemsQuery,
          orders: ordersQuery,
          orderItems: orderItemsQuery,
        },
        data: {
          parts: parts.data,
          shares: shares.data,
          quotes: quotes.data,
          quoteItems: quoteItems.data,
          orders: orders.data,
          orderItems: orderItems.data,
        },
        activeTab,
        setActiveTab,
        searchTerm,
        userHasTyped,
        setSearchTerm: (termUpdate) => {
          const newTerm =
            typeof termUpdate === 'string'
              ? termUpdate
              : termUpdate(searchTerm);
          setUserHasTyped(true);
          setSearchTerm(newTerm);
        },
      }}
    >
      {children}
    </SearchDataContext.Provider>
  );
};

export const useSearchDataContext = () => {
  const context = useContext(SearchDataContext);

  if (context == null) {
    throw new Error(
      'useSearchDataContext: `context` is undefined. Seems you forgot to wrap all Search components within `<SearchDataContextProvider />`',
    );
  }

  return context;
};
